import { EventClickArg, EventSourceInput } from '@fullcalendar/core';
import deLocale from '@fullcalendar/core/locales/de';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Messages } from '../constants';
import { useAlert } from '../context/AlertContext';
import { LoadingSpinner } from '../core/LoadingSpinner';
import { initEvent } from '../Interfaces/Calendar/ICalendarEvent';
import { ICalendarEventForm } from '../Interfaces/Calendar/ICalendarEventForm';
import { CalendarWrapper } from './CalendarWrapper';
import { EventFormDialog } from './EventFormDialog';
import { LoginPrompt } from './LoginPrompt';
import { transformEvent } from './utilities';
import { useGetCalendarEvents } from '../hooks/calendarHooks';
import { useGetLegalClients } from '../hooks/reactQuerys/LegalClientQueries';

export const CalendarMain: React.FC = () => {
  const { showAlert } = useAlert();

  const userId = Number(localStorage.getItem('idUser'));

  const [isMount, setIsMount] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<ICalendarEventForm>();
  const [selectedDate, setSelectedDate] = useState<string>();
  const [events, setEvents] = useState<ICalendarEventForm[]>();

  const {
    calendarEvents,
    isFetchingCalendarEvents,
    isErrorCalendarEvents,
    refetchCalendarEvents,
  } = useGetCalendarEvents(userId);

  const { legalClients, isLoadingLegalClients } = useGetLegalClients();

  useEffect(() => {
    if (legalClients === undefined || calendarEvents === undefined) return;
    setEvents(calendarEvents.map(e => transformEvent(e, legalClients)));
  }, [
    legalClients,
    calendarEvents,
    isFetchingCalendarEvents,
    isErrorCalendarEvents,
  ]);

  const displayedEvents = useMemo<EventSourceInput | undefined>(
    () =>
      events?.map(event => {
        const start = event.start.dateTime;
        let end = event.end.dateTime;
        if (event.isAllDay) {
          // Bei ganztägigen Terminen: Enddatum um einen Tag erhöhen, damit der letzte Tag angezeigt wird.
          const endDate = new Date(event.end.dateTime);
          endDate.setDate(endDate.getDate() + 1);
          end = endDate.toISOString();
        }
        return {
          id: event.id,
          title: event.subject,
          start: start,
          end: end,
          allDay: event.isAllDay,
          extendedProps: {
            location: event.location?.displayName || '',
            description: event.body?.content || '',
          },
        };
      }),
    [events]
  );

  const onClickDate = useCallback((info: { dateStr: string }) => {
    setSelectedDate(info.dateStr);
    setSelectedEvent(undefined);
    setIsMount(true);
  }, []);

  const onClick = useCallback(
    ({ event: { id } }: EventClickArg) => {
      setSelectedEvent(events?.find(e => e.id === id));
      setIsMount(true);
    },
    [events]
  );

  const unmount = useCallback(
    (editedEvent?: ICalendarEventForm, isDeleted?: boolean) => {
      if (isDeleted) {
        setEvents(events!.filter(c => c.id !== editedEvent!.id));
      } else if (editedEvent && calendarEvents !== undefined) {
        setEvents(
          events!.map(c => (c.id === editedEvent.id ? editedEvent : c))
        );
      }

      setIsMount(false);
      setSelectedEvent(undefined);
      setSelectedDate(undefined);
    },
    [events, calendarEvents]
  );

  const onReload = useCallback(async () => {
    await refetchCalendarEvents();
    showAlert({
      text: Messages.ReloadSuccessfully,
    });
  }, [refetchCalendarEvents, showAlert]);

  if (isLoadingLegalClients) return <LoadingSpinner />;

  if (isErrorCalendarEvents)
    return (
      <LoginPrompt
        userId={userId}
        refetchCalendarEvents={refetchCalendarEvents}
      />
    );

  return (
    <>
      <CalendarWrapper>
        <div style={{ maxHeight: '800px', overflowY: 'auto' }}>
          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView='timeGridWeek'
            events={displayedEvents}
            locale={deLocale}
            dateClick={onClickDate}
            eventClick={onClick}
            headerToolbar={{
              left: 'prev,next today newEvent reload',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay',
            }}
            firstDay={1}
            height='auto'
            eventDisplay='block'
            dayMaxEvents={10}
            customButtons={{
              newEvent: {
                text: 'Neues Ereignis',
                click: () => setIsMount(true),
              },
              reload: {
                text: 'Aktualisieren',
                click: onReload,
              },
            }}
          />
        </div>
      </CalendarWrapper>
      {isMount && (
        <EventFormDialog
          userId={userId}
          defaultValues={
            selectedEvent ??
            initEvent(selectedDate ? new Date(selectedDate) : new Date())
          }
          unmount={unmount}
        />
      )}
    </>
  );
};
